import classNames from "classnames";
import { BookingDetailSidePanelProfile } from "PFApp/booking/parts/overview/detail_view/booking_detail/booking_detail_side_panel_context/booking_detail_side_panel_context.types";
import { Overbooking } from "PFApp/booking/types";
import { useTheme } from "PFApp/theme_context_provider/theme_context_provider_context";
import { ActivityWarning } from "PFCore/hooks/queries/activity";
import { CalendarRange, Id } from "PFTypes";
import { useTranslation } from "react-i18next";

import {
  DetailsPanelSource,
  DetailsPanelType,
  OverbookingPotentialBooking,
  OverbookingsFilterConfig,
  useDetailsPanelsActions
} from "../details_panel";
import { useOverbookingColor } from "./use_overbooking_color";
import css from "./warning_icons.module.scss";
import WarningTooltipIcon from "./warning_tooltip_icon";

const countMisalignments = (misalignments: ActivityWarning[], profileId?: Id) => {
  if (!profileId) {
    const flattenMisalignments = misalignments.flatMap(({ misalignments }) => misalignments);
    return flattenMisalignments.length;
  }
  const profileMisalignments =
    misalignments
      .filter(({ profileIds }) => profileIds.includes(profileId as number))
      .flatMap(({ misalignments }) => misalignments) ?? [];
  return profileMisalignments.length;
};

interface WarningIconsProps {
  overbookings: Overbooking[];
  overbookingsFilterConfig?: OverbookingsFilterConfig;
  overbookingsCalendarRange?: CalendarRange;
  misalignments?: ActivityWarning[];
  activityIds?: Id[];
  profileId: Id | undefined;
  suspendedProfileDetails?: BookingDetailSidePanelProfile;
  classes?: { icon?: string };
  shouldCloseOtherSidePanelsOnClick?: boolean;
  enableMisalignmentsGoToDate?: boolean;
  potentialBookings?: OverbookingPotentialBooking[];
  source?: DetailsPanelSource;
  arePotentialWarnings?: boolean;
}

const WarningIcons = ({
  overbookings,
  overbookingsFilterConfig,
  overbookingsCalendarRange,
  misalignments = [],
  activityIds,
  profileId,
  suspendedProfileDetails,
  shouldCloseOtherSidePanelsOnClick,
  enableMisalignmentsGoToDate,
  potentialBookings,
  source,
  arePotentialWarnings = false
}: WarningIconsProps) => {
  const { t } = useTranslation("bookingModule");
  const { themeVars } = useTheme();

  const overbookedCounter = overbookings.length;

  const misalignmentsCounter = countMisalignments(misalignments, profileId);

  const { overbookingColor } = useOverbookingColor({ overbookings });
  const { openDetailsPanel, closeAllDetailsPanels } = useDetailsPanelsActions();

  if (!overbookedCounter && !misalignmentsCounter) {
    return null;
  }

  return (
    <div className={css.iconsWrapper}>
      {!!overbookedCounter && (
        <WarningTooltipIcon
          title={t("rowsSidePanel.warningIcons.seeOverbookings")}
          icon="warning"
          label={t("workforce.overbookedWarning")}
          iconColor={overbookingColor.hex()}
          counter={overbookedCounter}
          classes={{
            counter: classNames(css.overbookedCounter, {
              [css.lightText]: overbookingColor.isDark()
            })
          }}
          onClick={
            profileId && overbookingsCalendarRange
              ? () => {
                  shouldCloseOtherSidePanelsOnClick && closeAllDetailsPanels();
                  openDetailsPanel(
                    {
                      type: DetailsPanelType.Overbookings,
                      id: Number(profileId),
                      overbookingsDetails: {
                        activityIdsContext: activityIds,
                        filterConfig: overbookingsFilterConfig,
                        calendarRange: overbookingsCalendarRange,
                        potentialBookings,
                        ...(arePotentialWarnings && { potentialOverbookings: overbookings })
                      },
                      suspendedProfileDetails,
                      source
                    },
                    true
                  );
                }
              : undefined
          }
        />
      )}
      {!!misalignmentsCounter && (
        <WarningTooltipIcon
          title={t("rowsSidePanel.warningIcons.seeMisalignments")}
          icon="calendar-delete"
          label={t("projects.misalignment")}
          counter={misalignmentsCounter}
          iconColor={themeVars["paletteOrange3"]}
          classes={{
            counter: css.misalignmentCounter
          }}
          onClick={() => {
            shouldCloseOtherSidePanelsOnClick && closeAllDetailsPanels();
            openDetailsPanel(
              {
                type: DetailsPanelType.Misalignments,
                id: "misalignments",
                misalignmentDetails: {
                  profileId,
                  activityIds: activityIds ?? [],
                  enableGoToDate: !!enableMisalignmentsGoToDate,
                  ...(arePotentialWarnings && { potentialMisalignments: misalignments })
                }
              },
              true
            );
          }}
        />
      )}
    </div>
  );
};

export default WarningIcons;
